import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useBriefing } from "./useBriefing";
import { LEGACY_STEP_MAP, QUESTIONS } from "./questions";
import QuestionScreen from "./QuestionScreen";
import WizardStepper from "./WizardStepper";
import BriefingSuccess from "./BriefingSuccess";

/**
 * BriefingWizard — conversational onboarding for law firms: one question per
 * screen, ~20 quick decisions in the same three phases as before. Questions
 * are declared in questions.jsx; this runner only sequences them (skip rules,
 * back history, resume) and persists answers through the same autosave.
 */

/** Resolve the question to resume at (maps old 10-step ids to question ids). */
function resolveInitialId(briefing) {
  const saved = briefing?.currentStep;
  const mapped = LEGACY_STEP_MAP[saved] || saved;
  return QUESTIONS.some((q) => q.id === mapped) ? mapped : QUESTIONS[0].id;
}

/**
 * Index of a question inside the visible list; when the question itself got
 * skipped by a newer answer, falls back to the nearest previous visible one.
 */
function visibleIndexOf(visible, id) {
  const direct = visible.findIndex((q) => q.id === id);
  if (direct >= 0) return direct;
  const all = QUESTIONS.findIndex((q) => q.id === id);
  for (let i = all - 1; i >= 0; i--) {
    const idx = visible.findIndex((q) => q.id === QUESTIONS[i].id);
    if (idx >= 0) return idx;
  }
  return 0;
}

export default function BriefingWizard({ clientId }) {
  const reduceMotion = useReducedMotion();
  const {
    briefing,
    isLoading,
    isError,
    autosave,
    setBriefing,
    complete,
    isCompleting,
    completeError,
  } = useBriefing(clientId);

  // Answers applied this session, merged over the fetched briefing. Keeps the
  // skip rules and buildPatch merges in sync ahead of the debounced autosave.
  const [patches, setPatches] = useState({});
  const [currentId, setCurrentId] = useState(() => resolveInitialId(briefing));
  const [history, setHistory] = useState([]);
  const [completedBriefing, setCompletedBriefing] = useState(null);
  const [confirmNoMeeting, setConfirmNoMeeting] = useState(false);

  const draft = useMemo(() => ({ ...(briefing || {}), ...patches }), [briefing, patches]);
  const visible = useMemo(
    () => QUESTIONS.filter((q) => !q.skip || !q.skip(draft)),
    [draft],
  );
  const currentIndex = visibleIndexOf(visible, currentId);
  const current = visible[currentIndex];

  // Latest state for the auto-advance timer (avoids stale closures).
  const stateRef = useRef({ visible, currentIndex });
  stateRef.current = { visible, currentIndex };
  const advanceTimer = useRef(null);
  useEffect(() => () => clearTimeout(advanceTimer.current), []);

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (isError || !briefing) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center text-sm text-muted-foreground">
        Não foi possível carregar o briefing. Recarregue a página.
      </div>
    );
  }

  function save(patch) {
    setPatches((p) => ({ ...p, ...patch }));
    autosave(patch);
  }

  function moveTo(question, { pushHistory = true } = {}) {
    clearTimeout(advanceTimer.current);
    if (!question || question.id === currentId) return;
    if (pushHistory) setHistory((h) => [...h, currentId]);
    setCurrentId(question.id);
    autosave({ currentStep: question.id });
  }

  function goNext() {
    const { visible: vis, currentIndex: idx } = stateRef.current;
    moveTo(vis[Math.min(vis.length - 1, idx + 1)]);
  }

  function goBack() {
    clearTimeout(advanceTimer.current);
    if (history.length > 0) {
      const prev = history[history.length - 1];
      setHistory((h) => h.slice(0, -1));
      setCurrentId(prev);
      autosave({ currentStep: prev });
      return;
    }
    moveTo(visible[Math.max(0, currentIndex - 1)], { pushHistory: false });
  }

  // Deep-link (Review blockers). Hidden targets resolve to the nearest
  // previous visible question via visibleIndexOf.
  function goToId(id) {
    moveTo(visible[visibleIndexOf(visible, id)]);
  }

  // Single-choice questions advance on their own — the selection registers
  // visually, then the next question slides in.
  function handleAnswered() {
    if (!current?.autoAdvance) return;
    clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(goNext, 350);
  }

  async function runComplete() {
    try {
      const updated = await complete();
      setCompletedBriefing(updated);
    } catch {
      // Validation blockers surface in the Review step via completeError.
    }
  }

  // Finishing without a confirmed meeting is allowed, but warn first: selecting
  // a slot only pre-reserves it (~10 min) — goLiveAt is set only after the user
  // confirms the booking in the kickoff step.
  function handleComplete() {
    if (!draft.goLiveAt) {
      setConfirmNoMeeting(true);
      return;
    }
    runComplete();
  }

  // Peak-end: success screen until the user chooses to enter the dashboard.
  if (completedBriefing) {
    return (
      <BriefingSuccess
        clientId={clientId}
        onDone={() => setBriefing(completedBriefing)}
      />
    );
  }

  const isLast = currentIndex === visible.length - 1;

  return (
    <div className="relative flex min-h-full flex-col overflow-hidden">
      {/* Depth: cyan light blobs behind the glass card */}
      <div
        aria-hidden="true"
        className={cn(
          "tw-blob-cyan absolute -right-24 -top-16 h-72 w-72",
          !reduceMotion && "animate-float-soft",
        )}
      />
      <div
        aria-hidden="true"
        className="tw-blob-cyan absolute -bottom-24 -left-20 h-64 w-64 opacity-70"
      />

      {/* my-auto centers the block vertically when it's shorter than the
          viewport (no dead space below); when it's taller, the auto margins
          collapse and the parent scrolls — so nothing is ever clipped. */}
      <div className="relative mx-auto my-auto w-full max-w-3xl px-4 py-10 sm:py-14">
        {/* Halo heading */}
        <header className="mb-8">
          <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            Configure seu atendimento
          </h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground sm:text-base">
            
          </p>
        </header>

        <WizardStepper steps={QUESTIONS} currentId={current.id} />

        <AnimatePresence mode="wait">
          {current.Component ? (
            <current.Component
              key={current.id}
              clientId={clientId}
              briefing={draft}
              autosave={save}
              completeError={completeError}
              onJumpToStep={goToId}
            />
          ) : (
            <QuestionScreen
              key={current.id}
              question={current}
              briefing={draft}
              save={save}
              onAnswered={handleAnswered}
            />
          )}
        </AnimatePresence>

        <div className="mt-6 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={goBack}
            disabled={currentIndex === 0 && history.length === 0}
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
          </Button>

          {isLast ? (
            <Button className="hover:rounded-2xl" onClick={handleComplete} disabled={isCompleting}>
              {isCompleting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              Concluir briefing
            </Button>
          ) : (
            <Button className="hover:rounded-2xl" onClick={goNext}>
              Avançar <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>

        <Dialog open={confirmNoMeeting} onOpenChange={setConfirmNoMeeting}>
          <DialogContent className="max-w-sm" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>Você não agendou o kickoff.</DialogTitle>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="ghost"
                disabled={isCompleting}
                onClick={() => {
                  setConfirmNoMeeting(false);
                  runComplete();
                }}
              >
                Concluir sem agendar
              </Button>
              <Button
                onClick={() => {
                  setConfirmNoMeeting(false);
                  goToId("kickoff");
                }}
              >
                Voltar e agendar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
